import React from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import SEOHelmet from '../components/SEOHelmet';

const TermsOfService: React.FC = () => {
  usePageTitle("Terms of Service");

  return (
    <>
      <SEOHelmet
        title="Terms of Service"
        description="Terms of Service for CEC Track - Professional continuing education credits management platform. Review our user agreement and service terms."
        canonical="https://cectrack.com/terms-of-service"
        noindex={false}
      />
      <div className="container mt-4 mb-5">
      <h1 className="mb-4">Terms of Service</h1>
      <p className="text-muted"><strong>Last Updated:</strong> November 5, 2025</p>

      <div className="mt-4">
        <h2 className="h4 mt-4">1. Acceptance of Terms</h2>
        <p>
          By accessing and using CEC Track ("the Service"), you accept and agree to be bound by the terms and provisions of this agreement. 
          If you do not agree to these Terms of Service, please do not use the Service.
        </p>

        <h2 className="h4 mt-4">2. Description of Service</h2>
        <p>
          CEC Track provides a web-based platform for professionals to track and manage their Continuing Education Credits (CEC), 
          including but not limited to:
        </p>
        <ul>
          <li>Recording and organizing professional development classes and courses</li>
          <li>Managing professional licenses across multiple states</li>
          <li>Tracking renewal cycles and credit requirements</li>
          <li>Storing and managing course certificates and documentation</li>
          <li>Monitoring compliance with state-specific licensing requirements</li>
        </ul>

        <h2 className="h4 mt-4">3. User Accounts</h2>
        <p>
          To use the Service, you must create an account. You agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain and promptly update your account information</li>
          <li>Maintain the security of your password and account</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>

        <h2 className="h4 mt-4">4. User Responsibilities</h2>
        <p>
          You are solely responsible for:
        </p>
        <ul>
          <li>The accuracy and completeness of all information you enter into the Service</li>
          <li>Verifying that your education credits meet your licensing board's requirements</li>
          <li>Maintaining compliance with all applicable professional licensing regulations</li>
          <li>Keeping backup copies of important certificates and documentation</li>
          <li>Meeting all deadlines and requirements set by your licensing authorities</li>
        </ul>
        <p>
          <strong>Important:</strong> CEC Track is a tracking and organizational tool only. We do not verify credit 
          eligibility, validate course providers, or guarantee acceptance by licensing boards.
        </p>

        <h2 className="h4 mt-4">5. Acceptable Use</h2>
        <p>
          You agree not to:
        </p>
        <ul>
          <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
          <li>Attempt to gain unauthorized access to any portion of the Service</li>
          <li>Interfere with or disrupt the Service or servers connected to the Service</li>
          <li>Upload or transmit viruses, malware, or other malicious code</li>
          <li>Share your account credentials with unauthorized third parties</li>
          <li>Use automated systems to access the Service without our permission</li>
        </ul>

        <h2 className="h4 mt-4">6. Intellectual Property</h2>
        <p>
          The Service and its original content, features, and functionality are owned by CEC Track, Inc. and are 
          protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
        </p>
        <p>
          You retain ownership of all data and content you upload to the Service, including class information, 
          certificates, and personal information.
        </p>

        <h2 className="h4 mt-4">7. Data Storage and Certificates</h2>
        <p>
          We store your uploaded certificates and documentation using secure cloud storage services. While we take 
          reasonable measures to protect your data, we strongly recommend:
        </p>
        <ul>
          <li>Maintaining backup copies of all important certificates and documents</li>
          <li>Keeping original documentation from course providers</li>
          <li>Regularly downloading and archiving your data from the Service</li>
        </ul>

        <h2 className="h4 mt-4">8. Service Availability</h2>
        <p>
          We strive to provide reliable service but cannot guarantee uninterrupted access. We reserve the right to:
        </p>
        <ul>
          <li>Modify, suspend, or discontinue the Service with or without notice</li>
          <li>Perform scheduled and emergency maintenance</li>
          <li>Limit certain features or access to parts of the Service</li>
        </ul>
        <p>
          We are not liable for any interruption of service or loss of data resulting from maintenance, technical 
          issues, or circumstances beyond our control.
        </p>

        <h2 className="h4 mt-4">9. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
          INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR 
          NON-INFRINGEMENT.
        </p>
        <p>
          We do not warrant that:
        </p>
        <ul>
          <li>The Service will meet your specific requirements</li>
          <li>The Service will be uninterrupted, timely, secure, or error-free</li>
          <li>The results obtained from using the Service will be accurate or reliable</li>
          <li>Any data stored in the Service will be immune from loss or corruption</li>
        </ul>

        <h2 className="h4 mt-4">10. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, CEC TRACK, INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
        </p>
        <ul>
          <li>Loss of profits, data, or business opportunities</li>
          <li>Missed deadlines for license renewal or credit submission</li>
          <li>Professional sanctions or disciplinary actions</li>
          <li>Costs of obtaining substitute services</li>
        </ul>
        <p>
          Our total liability for any claim arising out of or relating to these Terms or the Service shall not 
          exceed the amount you paid us in the twelve (12) months preceding the claim.
        </p>

        <h2 className="h4 mt-4">11. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless CEC Track, Inc., its officers, directors, employees, and agents 
          from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
        </p>
        <ul>
          <li>Your use of the Service</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any rights of another party</li>
          <li>Any content you submit to the Service</li>
        </ul>

        <h2 className="h4 mt-4">12. Privacy</h2>
        <p>
          Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy to 
          understand how we collect, use, and protect your personal information.
        </p>

        <h2 className="h4 mt-4">13. Modifications to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify users of material changes via email 
          or through the Service. Your continued use of the Service after such modifications constitutes your 
          acceptance of the updated Terms.
        </p>

        <h2 className="h4 mt-4">14. Termination</h2>
        <p>
          We may terminate or suspend your account and access to the Service immediately, without prior notice or 
          liability, for any reason, including breach of these Terms.
        </p>
        <p>
          Upon termination, your right to use the Service will immediately cease. We will provide reasonable 
          opportunity to download your data before permanent deletion.
        </p>

        <h2 className="h4 mt-4">15. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of New York, 
          without regard to its conflict of law provisions.
        </p>

        <h2 className="h4 mt-4">16. Dispute Resolution</h2>
        <p>
          Any dispute arising from these Terms or the Service shall first be attempted to be resolved through good 
          faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration in 
          accordance with the rules of the American Arbitration Association.
        </p>

        <h2 className="h4 mt-4">17. Severability</h2>
        <p>
          If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or 
          eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
        </p>

        <h2 className="h4 mt-4">18. Contact Information</h2>
        <p>
          If you have any questions about these Terms of Service, please contact us at:
        </p>
        <p>
          <strong>CEC Track, Inc.</strong><br />
          Email: support@cectrack.com
        </p>
      </div>
      </div>
    </>
  );
};

export default TermsOfService;
